import { PriceAvailabilityResponse } from 'services/priceAvailability/types';
import {
  DealerPriceAvailabilityResponse,
  ProductDealerPriceAvailability
} from './types';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { defaultItem } from './constants';

const getPriceValue = (value: string, validationValue: string) => {
  const isValidNumber = /^[+-]?\d+(\.\d+)?$/.test(validationValue);
  const isValidValue = isValidNumber ? parseFloat(validationValue) > 0 : false;

  if (isEmpty(validationValue) || !isValidValue) {
    return '';
  }

  return value;
};

const transformPriceItem = (item: ProductDealerPriceAvailability) => {
  return {
    ...item,
    price: {
      unitPrice: getPriceValue(item.unitPrice, item.unformattedUnitPrice),
      discountedPrice: item.discountedPrice,
      discountAmount: item.discountedAmount,
      coreCharges: getPriceValue(item.coreCharges, item.unformattedCoreCharges),
      totalPrice: getPriceValue(item.totalPrice, item.unformattedTotalPrice)
    },
    availability: item.availability?.map(
      ({ availabilityMessage, quantityAvailable, showContactDealerPopUp }) => ({
        availabilityMessage: availabilityMessage,
        quantityAvailable: Number(quantityAvailable),
        showContactDealerPopUp
      })
    ),
    partLevelFlags: {
      isNonReturnable: item.nonReturnable,
      isCatPart: item.isCatPart,
      isIndirectReplacement: item.indirectReplacement,
      isAlternatePartsExist: item.alternatePartsExist,
      isAddToCartEnabled: item.enableAddToCart,
      isRemanPart: item.reman,
      isHoseAssemblyPart: item.hoseAssemblyIndicator
    },
    weight: !isEmpty(item.weight) ? +item.weight : null
  };
};

export const transformPriceAndAvailabilityHeadless = (
  priceData,
  partNumber
) => {
  const productPriceAvailability = priceData?.items?.find(
    (data) => data.partNumber === partNumber
  );

  return {
    isGstAvailable: priceData.isGstAvailable,
    gstIncludesTax: priceData.isGstIncludesTax,
    currency: priceData.currency,
    item: {
      ...productPriceAvailability,
      price: {
        unitPrice: getPriceValue(
          productPriceAvailability.price.formatted.undiscountedPrice,
          productPriceAvailability.price.unformatted.undiscountedPrice
        ),
        discountedPrice:
          productPriceAvailability.price.unformatted.discountedPrice,
        discountAmount:
          productPriceAvailability.price.unformatted.discountAmount.toString(),
        coreCharges: getPriceValue(
          productPriceAvailability.price.formatted.coreCharges,
          productPriceAvailability.price.unformatted.coreCharges
        ),
        totalPrice: getPriceValue(
          productPriceAvailability.price.formatted.totalPrice,
          productPriceAvailability.price.unformatted.totalPrice
        ),
        unformattedTotalPrice:
          productPriceAvailability.price.unformatted.totalPrice
      },
      replacementParts: productPriceAvailability.replacementParts,
      availability: productPriceAvailability.availability?.map(
        ({
          availabilityMessage,
          quantityAvailable,
          showDealerContactInformation
        }) => ({
          availabilityMessage: availabilityMessage,
          quantityAvailable: Number(quantityAvailable),
          showContactDealerPopUp: showDealerContactInformation
        })
      )
    }
  };
};

export const transformPriceAndAvailability = (
  priceData: DealerPriceAvailabilityResponse,
  partNumber?: string
): PriceAvailabilityResponse => {
  const { priceAndAvailability, ...rest } = priceData;

  if (partNumber) {
    const productPriceAvailability = priceData?.priceAndAvailability?.find(
      (data) => data.partNumber === partNumber
    );
    if (!isEmpty(productPriceAvailability)) {
      return {
        ...rest,
        item: transformPriceItem(productPriceAvailability)
      };
    }

    return {
      ...rest,
      item: defaultItem
    };
  }

  return {
    ...rest,
    items: priceAndAvailability?.map((item) => transformPriceItem(item))
  };
};
